import { oc } from "@orpc/contract";
import { z } from "zod";
import { LIBRARY_ITEM_KINDS, savedElementsSchema, savedLookSchema } from "../blocks/saved";
import { BRAND_LOGO_VARIANTS, brandTokensSchema } from "../brand/tokens";
import { FOLDER_NAME_MAX_LENGTH } from "../domain/folders";
import { ASSET_KINDS } from "../domain/media";
import { PLAN_IDS } from "../domain/plans";
import { ORG_ROLES } from "../domain/roles";
import {
  colorSchema,
  deviceFinishSchema,
  deviceIdSchema,
  deviceTiltSchema,
  type SceneDocument,
  sceneDocumentSchema,
  swatchSchema,
} from "../scene/schema";
import {
  adminOrgDetailSchema,
  adminOrgSchema,
  adminSessionSchema,
  adminUserDetailSchema,
  adminUserSchema,
  alertSchema,
  auditEntrySchema,
  backupRunSchema,
  failedJobSchema,
  legalPageSchema,
  legalSettingsSchema,
  metricSampleSchema,
  opsStatusSchema,
  queueStatsSchema,
  trafficStatsSchema,
} from "./admin";
import {
  commentAnchorSchema,
  commentBodySchema,
  commentSchema,
  guestNameSchema,
  SHARE_ACCESS,
  shareLinkSchema,
  shareTokenSchema,
  shareViewSchema,
} from "./collab";
import { contentPlanContract } from "./content-plan";
import {
  mentionableSchema,
  NOTIFICATION_EMAIL_CATEGORIES,
  notificationListSchema,
  notificationPreferencesSchema,
} from "./notifications";
import {
  activitySchema,
  adminCancellationSchema,
  assetFolderSchema,
  assetSchema,
  billingConsentInputSchema,
  brandKitSchema,
  brandKitSummarySchema,
  cancellableSchema,
  cancellationInputSchema,
  cancellationReceiptSchema,
  colorReportSchema,
  documentDetailSchema,
  documentSummarySchema,
  documentVersionSchema,
  exportRequestSchema,
  exportSchema,
  fontSchema,
  googleFontSchema,
  libraryItemSchema,
  orgDetailSchema,
  orgPlanSchema,
  orgSummarySchema,
  presenceSchema,
  projectSchema,
  searchResultsSchema,
  storageUsageSchema,
  systemInfoSchema,
  TRANSCRIPT_LANGUAGES,
  transcriptSchema,
  trashItemSchema,
} from "./schemas";

/**
 * API contract shared by frontend and backend (contract-first via oRPC).
 * The backend implements it in `apps/backend/src/api/router.ts`, the frontend
 * derives a fully typed client from it.
 */

const org = { orgId: z.string().min(1) };
const libraryName = z.string().trim().min(1, "Bitte einen Namen angeben").max(120);
const name = z.string().trim().min(1, "Bitte einen Namen angeben").max(200);
const folderName = z
  .string()
  .trim()
  .min(1, "Bitte einen Namen angeben")
  .max(FOLDER_NAME_MAX_LENGTH, `Höchstens ${FOLDER_NAME_MAX_LENGTH} Zeichen`);
/** Folder of the media library; null = top level. */
const folderId = z.uuid().nullable();

export const contract = {
  system: {
    info: oc.output(systemInfoSchema),
  },
  orgs: {
    list: oc.output(z.array(orgSummarySchema)),
    get: oc.input(z.object(org)).output(orgDetailSchema),
  },
  /** ⌘K: files (titles and texts), projects, media, own templates and saved blocks. */
  search: {
    query: oc
      .input(
        z.object({
          ...org,
          query: z.string().trim().min(1).max(100),
          limit: z.number().int().min(1).max(20).default(8),
        }),
      )
      .output(searchResultsSchema),
  },
  comments: {
    list: oc.input(z.object({ ...org, documentId: z.uuid() })).output(z.array(commentSchema)),
    create: oc
      .input(
        z
          .object({ ...org, documentId: z.uuid(), body: commentBodySchema })
          .extend(commentAnchorSchema.shape),
      )
      .output(commentSchema),
    /** Edit your own comment and/or resolve / reopen a thread. */
    update: oc
      .input(
        z.object({
          ...org,
          commentId: z.uuid(),
          body: commentBodySchema.optional(),
          resolved: z.boolean().optional(),
        }),
      )
      .output(commentSchema),
    /** Own comments; editors can delete any. A thread's first comment takes its replies along. */
    remove: oc
      .input(z.object({ ...org, commentId: z.uuid() }))
      .output(z.object({ ok: z.literal(true) })),
    /** Members that can be mentioned with @ (autocomplete of the comment field). */
    mentionable: oc.input(z.object(org)).output(z.array(mentionableSchema)),
  },
  /** Content calendar „Planung“ (api/content-plan.ts). */
  contentPlan: contentPlanContract,
  /** The caller's notifications across all organizations (bell in the app). */
  notifications: {
    /** Newest first; `before` = `nextCursor` of the previous page. */
    list: oc
      .input(
        z.object({
          limit: z.number().int().min(1).max(100).default(30),
          before: z.date().optional(),
        }),
      )
      .output(notificationListSchema),
    /** For the badge (polled). */
    unread: oc.output(z.object({ count: z.number() })),
    /** Marks the given notifications (or all) as read. */
    markRead: oc
      .input(
        z.union([
          z.object({ ids: z.array(z.uuid()).min(1).max(200) }),
          z.object({ all: z.literal(true) }),
        ]),
      )
      .output(z.object({ ok: z.literal(true), unread: z.number() })),
    preferences: {
      get: oc.output(notificationPreferencesSchema),
      /** Changes e-mail categories; missing ones stay as they are. */
      update: oc
        .input(
          z.object({
            email: z.partialRecord(z.enum(NOTIFICATION_EMAIL_CATEGORIES), z.boolean()),
          }),
        )
        .output(notificationPreferencesSchema),
    },
  },
  /** Share links of a file (open without an account). */
  shares: {
    list: oc.input(z.object({ ...org, documentId: z.uuid() })).output(z.array(shareLinkSchema)),
    create: oc
      .input(
        z.object({
          ...org,
          documentId: z.uuid(),
          label: z.string().trim().max(120).optional(),
          access: z.enum(SHARE_ACCESS).default("view"),
          /** null = does not expire. */
          expiresInDays: z.number().int().min(1).max(365).nullable().default(null),
        }),
      )
      .output(shareLinkSchema),
    update: oc
      .input(
        z.object({
          ...org,
          linkId: z.uuid(),
          label: z.string().trim().max(120).nullable().optional(),
          access: z.enum(SHARE_ACCESS).optional(),
        }),
      )
      .output(shareLinkSchema),
    revoke: oc
      .input(z.object({ ...org, linkId: z.uuid() }))
      .output(z.object({ ok: z.literal(true) })),
  },
  /** Public: what a share link shows, and comments of its guests. */
  share: {
    view: oc.input(z.object({ token: shareTokenSchema })).output(shareViewSchema),
    comment: oc
      .input(
        z
          .object({ token: shareTokenSchema, name: guestNameSchema, body: commentBodySchema })
          .extend(commentAnchorSchema.omit({ elementId: true }).shape),
      )
      .output(commentSchema),
  },
  projects: {
    list: oc
      .input(z.object({ ...org, includeArchived: z.boolean().default(false) }))
      .output(z.array(projectSchema)),
    get: oc.input(z.object({ ...org, projectId: z.uuid() })).output(projectSchema),
    create: oc
      .input(
        z.object({
          ...org,
          name,
          description: z.string().max(2000).optional(),
          brandKitId: z.uuid().nullable().optional(),
        }),
      )
      .output(projectSchema),
    update: oc
      .input(
        z.object({
          ...org,
          projectId: z.uuid(),
          name: name.optional(),
          description: z.string().max(2000).nullable().optional(),
          archived: z.boolean().optional(),
          /** null = use the organization's default kit. */
          brandKitId: z.uuid().nullable().optional(),
        }),
      )
      .output(projectSchema),
    remove: oc
      .input(z.object({ ...org, projectId: z.uuid() }))
      .output(z.object({ ok: z.literal(true) })),
  },
  documents: {
    list: oc
      .input(z.object({ ...org, projectId: z.uuid() }))
      .output(z.array(documentSummarySchema)),
    recent: oc
      .input(z.object({ ...org, limit: z.number().int().min(1).max(50).default(12) }))
      .output(z.array(documentSummarySchema.extend({ projectName: z.string() }))),
    get: oc.input(z.object({ ...org, documentId: z.uuid() })).output(documentDetailSchema),
    create: oc
      .input(
        z
          .object({
            ...org,
            projectId: z.uuid(),
            title: name,
            presetId: z.string().optional(),
            /** Size for `presetId: "custom"` (pixels). */
            width: z.number().int().min(16).max(20000).optional(),
            height: z.number().int().min(16).max(20000).optional(),
            /** Designs: open on a board (all pages side by side). */
            board: z.boolean().optional(),
            /** Full content, e.g. "save my version as a copy" after a conflict. */
            content: sceneDocumentSchema.optional(),
            /** Copies the content of a template of the same organization. */
            templateId: z.uuid().optional(),
            /** Builds a template of the built-in library (`LIBRARY_TEMPLATES`) in the project's kit. */
            libraryTemplateId: z.string().min(1).max(80).optional(),
            /** Library templates without a kit: design style (`BRAND_STYLES`) instead of the template's own. */
            libraryStyle: z.string().min(1).max(40).optional(),
          })
          .refine(
            (input) =>
              input.presetId || input.content || input.templateId || input.libraryTemplateId,
            "Format, Vorlage oder Inhalt angeben",
          ),
      )
      .output(documentSummarySchema),
    update: oc
      .input(
        z.object({
          ...org,
          documentId: z.uuid(),
          title: name.optional(),
          content: sceneDocumentSchema.optional(),
          /** Required together with `content`: optimistic locking. */
          revision: z.number().int().optional(),
          isTemplate: z.boolean().optional(),
        }),
      )
      .output(documentSummarySchema),
    /** Copy of the document in another format (e.g. Post → Story), in the same project. */
    transfer: oc
      .input(
        z.object({
          ...org,
          documentId: z.uuid(),
          presetId: z.string(),
          title: name.optional(),
          /** Size for `presetId: "custom"` (pixels). */
          width: z.number().int().min(50).max(10000).optional(),
          height: z.number().int().min(50).max(10000).optional(),
        }),
      )
      .output(documentSummarySchema),
    duplicate: oc.input(z.object({ ...org, documentId: z.uuid() })).output(documentSummarySchema),
    move: oc
      .input(z.object({ ...org, documentId: z.uuid(), projectId: z.uuid() }))
      .output(documentSummarySchema),
    remove: oc
      .input(z.object({ ...org, documentId: z.uuid() }))
      .output(z.object({ ok: z.literal(true) })),
    /** Heartbeat while a document is open; returns who else has it open. */
    presence: oc
      .input(z.object({ ...org, documentId: z.uuid(), mode: z.enum(["edit", "view", "leave"]) }))
      .output(z.object({ viewers: z.array(presenceSchema) })),
    versions: {
      list: oc
        .input(z.object({ ...org, documentId: z.uuid() }))
        .output(z.array(documentVersionSchema)),
      get: oc
        .input(z.object({ ...org, versionId: z.uuid() }))
        .output(documentVersionSchema.extend({ content: z.custom<SceneDocument>() })),
      create: oc
        .input(z.object({ ...org, documentId: z.uuid(), label: name }))
        .output(documentVersionSchema),
      restore: oc
        .input(z.object({ ...org, documentId: z.uuid(), versionId: z.uuid() }))
        .output(documentSummarySchema),
    },
  },
  exports: {
    create: oc
      .input(z.object({ ...org, documentId: z.uuid() }).and(exportRequestSchema))
      .output(exportSchema),
    get: oc.input(z.object({ ...org, exportId: z.uuid() })).output(exportSchema),
    list: oc.input(z.object({ ...org, documentId: z.uuid() })).output(z.array(exportSchema)),
  },
  print: {
    /** CMYK values, soft proof and gamut warning for colours (colour picker). */
    colors: oc
      .input(
        z.object({
          ...org,
          profile: z.string().max(50),
          colors: z.array(colorSchema).min(1).max(64),
          swatches: z.array(swatchSchema).max(200).optional(),
        }),
      )
      .output(z.array(colorReportSchema)),
    /** Screen colour for exact CMYK values (for brand swatches). */
    proof: oc
      .input(
        z.object({
          ...org,
          profile: z.string().max(50),
          cmyk: z.tuple([
            z.number().min(0).max(100),
            z.number().min(0).max(100),
            z.number().min(0).max(100),
            z.number().min(0).max(100),
          ]),
        }),
      )
      .output(z.object({ color: z.string() })),
  },
  media: {
    list: oc
      .input(
        z.object({
          ...org,
          kind: z.enum(ASSET_KINDS).optional(),
          /** Several kinds at once (the editor's media library); combined with `kind` by AND. */
          kinds: z.array(z.enum(ASSET_KINDS)).min(1).max(ASSET_KINDS.length).optional(),
          projectId: z.uuid().optional(),
          /** Omitted = all folders, null = only media that is not in a folder. */
          folderId: folderId.optional(),
          search: z.string().max(200).optional(),
          limit: z.number().int().min(1).max(200).default(60),
          offset: z.number().int().min(0).default(0),
        }),
      )
      .output(z.object({ items: z.array(assetSchema), total: z.number() })),
    get: oc.input(z.object({ ...org, assetId: z.uuid() })).output(assetSchema),
    update: oc
      .input(
        z.object({
          ...org,
          assetId: z.uuid(),
          name: name.optional(),
          projectId: z.uuid().nullable().optional(),
          folderId: folderId.optional(),
        }),
      )
      .output(assetSchema),
    /** Moves several media files into a folder at once (multi-select, drag & drop). */
    move: oc
      .input(z.object({ ...org, assetIds: z.array(z.uuid()).min(1).max(500), folderId }))
      .output(z.object({ ok: z.literal(true), moved: z.number() })),
    remove: oc
      .input(z.object({ ...org, assetId: z.uuid() }))
      .output(z.object({ ok: z.literal(true) })),
    /** Several files into the trash at once (selection in the media library). */
    removeMany: oc
      .input(z.object({ ...org, assetIds: z.array(z.uuid()).min(1).max(500) }))
      .output(z.object({ ok: z.literal(true), removed: z.number() })),
    folders: {
      /** All folders of the organization, flat (tree via `parentId`). */
      list: oc.input(z.object(org)).output(z.array(assetFolderSchema)),
      create: oc
        .input(z.object({ ...org, name: folderName, parentId: folderId.default(null) }))
        .output(assetFolderSchema),
      /** Renames and/or moves a folder (not into itself or one of its subfolders). */
      update: oc
        .input(
          z.object({
            ...org,
            folderId: z.uuid(),
            name: folderName.optional(),
            parentId: folderId.optional(),
          }),
        )
        .output(assetFolderSchema),
      /** Deletes the folder only: its media and subfolders move up to the parent folder. */
      remove: oc
        .input(z.object({ ...org, folderId: z.uuid() }))
        .output(z.object({ ok: z.literal(true) })),
    },
    /** Transcript of a video (null: never transcribed, e.g. no audio). */
    transcript: oc
      .input(z.object({ ...org, assetId: z.uuid() }))
      .output(transcriptSchema.nullable()),
    /** (Re-)transcribes a video locally with whisper.cpp. */
    transcribe: oc
      .input(z.object({ ...org, assetId: z.uuid(), language: z.enum(TRANSCRIPT_LANGUAGES) }))
      .output(transcriptSchema),
    /**
     * Removes the background of an image locally on the server ("Freistellen").
     * Returns the cut-out copy – a new PNG in the same folder, the original
     * stays. Asking again for the same image returns the existing copy (also
     * while it is still being made, `status: "processing"`).
     */
    removeBackground: oc.input(z.object({ ...org, assetId: z.uuid() })).output(assetSchema),
    /**
     * Cuts the person out of every frame of a video ("Person freistellen"),
     * locally on the server. Returns the video; `matte` shows the progress.
     * Asking again while it runs or once it is done changes nothing.
     */
    cutOutPerson: oc.input(z.object({ ...org, assetId: z.uuid() })).output(assetSchema),
    /**
     * Saves a device mockup of an image as a new image in the media library:
     * the device alone on a transparent background, cropped to it. Returns
     * the new image with `status: "processing"`; the worker renders it.
     */
    createMockup: oc
      .input(
        z.object({
          ...org,
          assetId: z.uuid(),
          device: deviceIdSchema,
          finish: deviceFinishSchema.optional(),
          landscape: z.boolean().default(false),
          tilt: deviceTiltSchema.default({ x: 0, y: 0, z: 0 }),
        }),
      )
      .output(assetSchema),
  },
  /** Saved building blocks and colour looks of the organization ("Meine Bausteine", "Meine Looks"). */
  library: {
    list: oc
      .input(z.object({ ...org, kind: z.enum(LIBRARY_ITEM_KINDS) }))
      .output(z.array(libraryItemSchema)),
    create: oc
      .input(
        z.discriminatedUnion("kind", [
          z.object({
            ...org,
            kind: z.literal("element"),
            name: libraryName,
            data: savedElementsSchema,
          }),
          z.object({ ...org, kind: z.literal("look"), name: libraryName, data: savedLookSchema }),
        ]),
      )
      .output(libraryItemSchema),
    rename: oc
      .input(z.object({ ...org, itemId: z.uuid(), name: libraryName }))
      .output(libraryItemSchema),
    remove: oc
      .input(z.object({ ...org, itemId: z.uuid() }))
      .output(z.object({ ok: z.literal(true) })),
  },
  activity: {
    /** Running and just finished background work of the organization. */
    list: oc.input(z.object(org)).output(z.array(activitySchema)),
  },
  templates: {
    list: oc
      .input(z.object(org))
      .output(z.array(documentSummarySchema.extend({ projectName: z.string() }))),
  },
  brand: {
    /** Empty while the organization has no kit (kits are never created automatically). */
    list: oc.input(z.object(org)).output(z.array(brandKitSummarySchema)),
    get: oc.input(z.object({ ...org, kitId: z.uuid() })).output(brandKitSchema),
    /** The kit a project uses (its own or the organization's default kit); null without any kit. */
    effective: oc
      .input(z.object({ ...org, projectId: z.uuid().optional() }))
      .output(brandKitSchema.nullable()),
    /** The first kit of an organization becomes its default kit. */
    create: oc
      .input(
        z.object({
          ...org,
          name,
          copyFrom: z.uuid().optional(),
          /** Start from a design style (`BRAND_STYLES`): its colours, fonts and radii. */
          style: z.string().min(1).max(40).optional(),
        }),
      )
      .output(brandKitSchema),
    update: oc
      .input(
        z.object({
          ...org,
          kitId: z.uuid(),
          name: name.optional(),
          tokens: brandTokensSchema.optional(),
          isDefault: z.literal(true).optional(),
          /** Replaces all logos. */
          logos: z
            .array(z.object({ variant: z.enum(BRAND_LOGO_VARIANTS), assetId: z.uuid() }))
            .max(BRAND_LOGO_VARIANTS.length)
            .optional(),
          /** Replaces all graphics (in this order). */
          graphics: z.array(z.uuid()).max(200).optional(),
        }),
      )
      .output(brandKitSchema),
    /** The default kit can only go while it is the last kit. */
    remove: oc
      .input(z.object({ ...org, kitId: z.uuid() }))
      .output(z.object({ ok: z.literal(true) })),
  },
  fonts: {
    list: oc.input(z.object(org)).output(z.array(fontSchema)),
    /** Font files (TTF, OTF, WOFF, WOFF2); family, weight and style are read from the files. */
    upload: oc
      .input(
        z.object({
          ...org,
          files: z
            .array(
              z.file().max(15 * 1024 * 1024, "Schriftdateien dürfen höchstens 15 MB groß sein"),
            )
            .min(1)
            .max(24),
        }),
      )
      .output(z.array(fontSchema)),
    googleSearch: oc
      .input(z.object({ ...org, query: z.string().trim().max(100).default("") }))
      .output(z.array(googleFontSchema)),
    importGoogle: oc
      .input(z.object({ ...org, googleId: z.string().regex(/^[a-z0-9-]{1,80}$/) }))
      .output(fontSchema),
    remove: oc
      .input(z.object({ ...org, fontId: z.uuid() }))
      .output(z.object({ ok: z.literal(true) })),
  },
  trash: {
    list: oc.input(z.object(org)).output(z.array(trashItemSchema)),
    restore: oc
      .input(z.object({ ...org, type: z.enum(["project", "document", "asset"]), id: z.uuid() }))
      .output(z.object({ ok: z.literal(true) })),
    purge: oc
      .input(z.object({ ...org, type: z.enum(["project", "document", "asset"]), id: z.uuid() }))
      .output(z.object({ ok: z.literal(true), freedBytes: z.number() })),
  },
  storage: {
    usage: oc.input(z.object(org)).output(storageUsageSchema),
  },
  /** Plan, limits and this month's usage of the organization. */
  plan: {
    get: oc.input(z.object(org)).output(orgPlanSchema),
  },
  /** Ordering and cancelling subscriptions (docs/16-geschaeftsmodell.md). */
  billing: {
    /** Records the confirmed order summary; Stripe's checkout requires one. */
    consent: oc.input(billingConsentInputSchema).output(z.object({ ok: z.literal(true) })),
    /** Signed in: own organizations with a subscription that can be cancelled. */
    cancellable: oc.output(z.array(cancellableSchema)),
    /** Public: "Verträge hier kündigen". */
    cancel: oc.input(cancellationInputSchema).output(cancellationReceiptSchema),
  },
  /** Public: operator data for imprint, privacy policy and terms. */
  legal: {
    get: oc.output(legalPageSchema),
  },
  /**
   * Platform administration (docs/13-betrieb.md). Everything except `session` and
   * `unlock` requires the admin role, 2FA and a 2FA unlock of the current session.
   */
  admin: {
    session: oc.output(adminSessionSchema),
    unlock: oc
      .input(
        z.object({
          code: z.string().trim().min(6).max(32),
          method: z.enum(["totp", "backup"]).default("totp"),
        }),
      )
      .output(adminSessionSchema),
    lock: oc.output(z.object({ ok: z.literal(true) })),
    overview: oc.output(opsStatusSchema),
    /** Landing page visitors (docs/18-landingpage.md). */
    traffic: oc
      .input(z.object({ days: z.number().int().min(1).max(365).default(30) }))
      .output(trafficStatsSchema),
    metrics: oc
      .input(
        z.object({
          hours: z
            .number()
            .int()
            .min(1)
            .max(24 * 30)
            .default(24),
        }),
      )
      .output(z.array(metricSampleSchema)),
    users: {
      list: oc
        .input(
          z.object({
            query: z.string().trim().max(200).default(""),
            filter: z.enum(["all", "admins", "banned", "unverified", "no-org"]).default("all"),
            limit: z.number().int().min(1).max(200).default(50),
            offset: z.number().int().min(0).default(0),
          }),
        )
        .output(z.object({ items: z.array(adminUserSchema), total: z.number() })),
      get: oc.input(z.object({ userId: z.string().min(1) })).output(adminUserDetailSchema),
      ban: oc
        .input(
          z.object({
            userId: z.string().min(1),
            reason: z.string().trim().min(3, "Bitte einen Grund angeben").max(500),
            /** null = until lifted by hand. */
            days: z.number().int().min(1).max(3650).nullable().default(null),
          }),
        )
        .output(z.object({ ok: z.literal(true) })),
      unban: oc
        .input(z.object({ userId: z.string().min(1) }))
        .output(z.object({ ok: z.literal(true) })),
      revokeSessions: oc
        .input(z.object({ userId: z.string().min(1) }))
        .output(z.object({ ok: z.literal(true), revoked: z.number() })),
      setAdmin: oc
        .input(z.object({ userId: z.string().min(1), admin: z.boolean() }))
        .output(z.object({ ok: z.literal(true) })),
    },
    orgs: {
      list: oc
        .input(
          z.object({
            query: z.string().trim().max(200).default(""),
            limit: z.number().int().min(1).max(200).default(50),
            offset: z.number().int().min(0).default(0),
          }),
        )
        .output(z.object({ items: z.array(adminOrgSchema), total: z.number() })),
      get: oc.input(z.object(org)).output(adminOrgDetailSchema),
      setQuota: oc
        .input(z.object({ ...org, quotaGb: z.number().positive().max(100_000).nullable() }))
        .output(z.object({ ok: z.literal(true) })),
      setPlan: oc
        .input(z.object({ ...org, plan: z.enum(PLAN_IDS) }))
        .output(z.object({ ok: z.literal(true) })),
      /** Puts an existing account into the organization (no invitation mail needed). */
      addMember: oc
        .input(z.object({ ...org, userId: z.string().min(1), role: z.enum(ORG_ROLES) }))
        .output(z.object({ ok: z.literal(true) })),
      setMemberRole: oc
        .input(z.object({ ...org, userId: z.string().min(1), role: z.enum(ORG_ROLES) }))
        .output(z.object({ ok: z.literal(true) })),
      removeMember: oc
        .input(z.object({ ...org, userId: z.string().min(1) }))
        .output(z.object({ ok: z.literal(true) })),
    },
    jobs: {
      list: oc.output(
        z.object({ queues: z.array(queueStatsSchema), failed: z.array(failedJobSchema) }),
      ),
      retry: oc
        .input(z.object({ queue: z.string().min(1), jobId: z.string().min(1) }))
        .output(z.object({ ok: z.literal(true) })),
      remove: oc
        .input(z.object({ queue: z.string().min(1), jobId: z.string().min(1) }))
        .output(z.object({ ok: z.literal(true) })),
    },
    alerts: {
      list: oc
        .input(
          z.object({
            status: z.enum(["open", "all"]).default("all"),
            limit: z.number().int().min(1).max(500).default(100),
          }),
        )
        .output(z.array(alertSchema)),
      test: oc.output(z.object({ ok: z.literal(true), channels: z.array(z.string()) })),
    },
    backups: {
      list: oc
        .input(z.object({ limit: z.number().int().min(1).max(500).default(100) }))
        .output(z.array(backupRunSchema)),
    },
    audit: {
      list: oc
        .input(
          z.object({
            limit: z.number().int().min(1).max(200).default(50),
            offset: z.number().int().min(0).default(0),
            targetId: z.string().optional(),
          }),
        )
        .output(z.object({ items: z.array(auditEntrySchema), total: z.number() })),
    },
    legal: {
      update: oc.input(legalSettingsSchema).output(legalPageSchema),
    },
    /** Requests from "Verträge hier kündigen"; open ones need a look by hand. */
    cancellations: {
      list: oc
        .input(z.object({ onlyOpen: z.boolean().default(false) }))
        .output(z.array(adminCancellationSchema)),
      resolve: oc.input(z.object({ id: z.uuid() })).output(z.object({ ok: z.literal(true) })),
    },
  },
};

export type Contract = typeof contract;
